import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Star, Ticket, MessageSquare, Anchor } from "lucide-react";

function ratingPill(n) {
  if (n == null) return <span className="text-xs text-[#2B3F4D]">—</span>;
  const cls =
    n >= 5
      ? "bg-emerald-100 text-emerald-700"
      : n >= 4
      ? "bg-[#1DA3B3]/15 text-[#1DA3B3]"
      : n >= 3
      ? "bg-[#BD984D]/20 text-[#A57F39]"
      : "bg-red-100 text-red-700";
  return (
    <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-bold ${cls}`}>
      <Star className="w-3 h-3" /> {n}
    </span>
  );
}

function npsCategory(n) {
  if (n == null) return null;
  if (n >= 9) return { label: "Promoter", cls: "bg-emerald-100 text-emerald-700" };
  if (n >= 7) return { label: "Passive", cls: "bg-slate-100 text-slate-700" };
  return { label: "Detractor", cls: "bg-red-100 text-red-700" };
}

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  useEffect(() => {
    api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => {});
    api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
  }, []);

  // Aggregate averages for the dashboard header
  const ratings = surveys.filter((s) => !s.skipped);
  const avg = (key) => {
    const vals = ratings.map((s) => s[key]).filter((v) => v != null);
    if (vals.length === 0) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };
  const npsList = ratings.map((s) => s.nps).filter((v) => v != null);
  const npsScore = npsList.length === 0
    ? null
    : Math.round(
        100 * (npsList.filter((n) => n >= 9).length / npsList.length) -
          100 * (npsList.filter((n) => n <= 6).length / npsList.length),
      );

  return (
    <div className="space-y-6" data-testid="admin-surveys">
      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Tilbakemelding</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Kundeundersøkelser</h1>
        <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Sammendrag av kvalitetsundersøkelser etter utsjekk. Voucher-koder
          utstedes automatisk når kunden svarer på minst ett spørsmål.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Båt", v: avg("rating_boat") },
          { label: "Crew", v: avg("rating_crew") },
          { label: "Oppfølging", v: avg("rating_followup") },
          { label: "Utstyr", v: avg("rating_equipment") },
        ].map((s) => (
          <Card key={s.label} className="p-4 border border-[#E2E8F0] bg-white">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">{s.label}</div>
            <div className="text-2xl font-bold text-[#0C1824] mt-1">
              {s.v == null ? "—" : `${s.v} / 6`}
            </div>
          </Card>
        ))}
        <Card className="p-4 border border-[#E2E8F0] bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">NPS</div>
          <div className={`text-2xl font-bold mt-1 ${
            npsScore == null ? "text-[#0C1824]"
              : npsScore >= 50 ? "text-emerald-700"
              : npsScore >= 0 ? "text-[#A57F39]"
              : "text-red-600"
          }`}>
            {npsScore == null ? "—" : npsScore}
          </div>
        </Card>
      </div>

      <Tabs defaultValue="surveys">
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="surveys" data-testid="tab-surveys">
              <MessageSquare className="w-3.5 h-3.5 mr-1" />
              Undersøkelser ({surveys.length})
            </TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers">
              <Ticket className="w-3.5 h-3.5 mr-1" />
              Voucher-koder ({vouchers.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="surveys" className="mt-5 space-y-3">
          {surveys.length === 0 && (
            <Card className="p-6 text-sm text-[#2B3F4D]">
              Ingen undersøkelser mottatt ennå.
            </Card>
          )}
          {surveys.map((s) => {
            const nps = npsCategory(s.nps);
            return (
              <Card key={s.id} className="p-4" data-testid={`survey-row-${s.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0C1824]">
                        {s.anonymous ? "Anonym" : (s.customer_name || s.customer_email || "Kunde")}
                      </span>
                      {s.skipped && (
                        <Badge className="bg-slate-200 text-slate-700">Hoppet over</Badge>
                      )}
                      {nps && !s.skipped && (
                        <Badge className={nps.cls}>NPS {s.nps} · {nps.label}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-[#2B3F4D] mt-1">
                      {new Date(s.submitted_at).toLocaleString("nb-NO")}
                    </div>
                  </div>
                </div>
                {!s.skipped && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">Båt</div>
                      <div className="mt-1">{ratingPill(s.rating_boat)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">Crew</div>
                      <div className="mt-1">{ratingPill(s.rating_crew)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">Oppfølging</div>
                      <div className="mt-1">{ratingPill(s.rating_followup)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39]">Utstyr</div>
                      <div className="mt-1">{ratingPill(s.rating_equipment)}</div>
                    </div>
                  </div>
                )}
                {s.improvements && (
                  <div className="mt-3 p-2 rounded-md bg-[#F8F4ED] text-sm text-[#2B3F4D]">
                    <strong className="text-[#A57F39]">Bedre:</strong> {s.improvements}
                  </div>
                )}
                {s.suggestions && (
                  <div className="mt-2 p-2 rounded-md bg-[#F8F4ED] text-sm text-[#2B3F4D]">
                    <strong className="text-[#A57F39]">Annerledes:</strong> {s.suggestions}
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="vouchers" className="mt-5 space-y-2">
          {vouchers.length === 0 && (
            <Card className="p-6 text-sm text-[#2B3F4D]">
              Ingen voucher-koder utstedt ennå.
            </Card>
          )}
          {vouchers.map((v) => (
            <Card key={v.id} className="p-4" data-testid={`voucher-row-${v.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-base font-mono font-bold tracking-widest text-[#0C1824] bg-[#BD984D]/10 px-2 py-1 rounded">
                      {v.code}
                    </code>
                    <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3]">
                      {v.percent_discount}% rabatt
                    </Badge>
                    {v.used && (
                      <Badge className="bg-emerald-100 text-emerald-700">Brukt</Badge>
                    )}
                    {v.expired && (
                      <Badge className="bg-slate-200 text-slate-700">Utløpt</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-1.5">
                    {v.customer_name || v.customer_email || "—"}
                    {" · "}Utstedt {new Date(v.issued_at).toLocaleDateString("nb-NO")}
                    {" · "}Gyldig til {(v.valid_until || "").slice(0, 10)}
                  </div>
                  {v.used && (
                    <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <Anchor className="w-3 h-3" />
                      Brukt {new Date(v.used_at).toLocaleDateString("nb-NO")}
                      {v.redeemed_by_name && ` · ${v.redeemed_by_name}`}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
